/**
 * useAppCoordinator.ts
 * Hook React pour accéder à AppCoordinator de manière sécurisée
 * Garantit que AppCoordinator est disponible et lance une erreur claire si null
 */
import { useState, useEffect } from 'react';
import { appService } from '../services/AppService';
import type { AppCoordinatorType } from '../types/AppCoordinatorTypes';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Hook pour accéder à AppCoordinator de manière sécurisée
 * @throws Error si AppCoordinator n'est pas disponible
 * @returns AppCoordinator (jamais null)
 * CORRIGÉ: Utilise useSyncExternalStore pour se mettre à jour quand AppCoordinator devient disponible
 */
export function useAppCoordinator(): AppCoordinatorType {
  // CORRIGÉ: Approche simplifiée avec useState + useEffect + subscription
  // useSyncExternalStore ne fonctionne pas correctement dans ce contexte
  // On utilise une approche hybride : subscription + polling de secours
  const [coordinator, setCoordinator] = useState<AppCoordinatorType | null>(() => {
    const initial = appService.getAppCoordinator();
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'useAppCoordinator.ts:18',
          message: 'useAppCoordinator initial state',
          data: {
            coordinatorAvailable: initial !== null,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        });
      })
      .catch(() => {});
    // #endregion
    return initial;
  });

  useEffect(() => {
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'useAppCoordinator.ts:34',
          message: 'useAppCoordinator effect started',
          data: {
            coordinatorAvailable: coordinator !== null,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        });
      })
      .catch(() => {});
    // #endregion

    // Vérifier immédiatement
    const current = appService.getAppCoordinator();
    if (current !== coordinator) {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'useAppCoordinator.ts:47',
            message: 'useAppCoordinator immediate check found coordinator',
            data: {
              wasNull: coordinator === null,
              nowAvailable: current !== null,
            },
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'A',
          });
        })
        .catch(() => {});
      // #endregion
      setCoordinator(current);
    }

    // S'abonner aux changements (remplace le polling)
    const unsubscribe = appService.subscribe(newCoordinator => {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'useAppCoordinator.ts:61',
            message: 'useAppCoordinator subscription callback',
            data: {
              coordinatorAvailable: newCoordinator !== null,
              wasNull: coordinator === null,
            },
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'A',
          });
        })
        .catch(() => {});
      // #endregion
      setCoordinator(prev => {
        if (newCoordinator !== prev) {
          // #region agent log
          import('../utils/debugLogger')
            .then(({ debugLog }) => {
              debugLog({
                location: 'useAppCoordinator.ts:73',
                message: 'useAppCoordinator state updated from subscription',
                data: {
                  prevWasNull: prev === null,
                  newIsAvailable: newCoordinator !== null,
                },
                sessionId: 'debug-session',
                runId: 'run1',
                hypothesisId: 'A',
              });
            })
            .catch(() => {});
          // #endregion
          return newCoordinator;
        }
        return prev;
      });
    });

    return () => {
      unsubscribe();
    };
  }, []); // Pas de dépendances - on veut que l'effet ne s'exécute qu'une fois

  // #region agent log
  import('../utils/debugLogger')
    .then(({ debugLog }) => {
      debugLog({
        location: 'useAppCoordinator.ts:145',
        message: 'useAppCoordinator returning',
        data: {
          coordinatorAvailable: coordinator !== null,
        },
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'A',
      });
    })
    .catch(() => {});
  // #endregion

  // Lancer une erreur si AppCoordinator n'est pas disponible
  if (!coordinator) {
    const error = new Error(
      "AppCoordinator non disponible. Assurez-vous que useAppInitialization() a été appelé et que l'initialisation est terminée."
    );
    logger.error('❌ useAppCoordinator: AppCoordinator non disponible', {
      error,
      stack: error.stack,
    });
    throw error;
  }

  return coordinator;
}

/**
 * Hook pour accéder à AppCoordinator de manière optionnelle
 * Retourne null si AppCoordinator n'est pas disponible (au lieu de lancer une erreur)
 * CORRIGÉ: Utilise useSyncExternalStore pour une synchronisation fiable avec appService
 * @returns AppCoordinator | null
 */
export function useAppCoordinatorOptional(): AppCoordinatorType | null {
  // CORRIGÉ: Approche simplifiée avec useState + useEffect + subscription
  // useSyncExternalStore ne fonctionne pas correctement dans ce contexte
  // On utilise une approche hybride : subscription + polling de secours
  const [coordinator, setCoordinator] = useState<AppCoordinatorType | null>(() => {
    const initial = appService.getAppCoordinator();
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'useAppCoordinator.ts:122',
          message: 'useAppCoordinatorOptional initial state',
          data: {
            coordinatorAvailable: initial !== null,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        });
      })
      .catch(() => {});
    // #endregion
    return initial;
  });

  useEffect(() => {
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'useAppCoordinator.ts:138',
          message: 'useAppCoordinatorOptional effect started',
          data: {
            coordinatorAvailable: coordinator !== null,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A',
        });
      })
      .catch(() => {});
    // #endregion

    // Vérifier immédiatement
    const current = appService.getAppCoordinator();
    if (current !== coordinator) {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'useAppCoordinator.ts:151',
            message: 'useAppCoordinatorOptional immediate check found coordinator',
            data: {
              wasNull: coordinator === null,
              nowAvailable: current !== null,
            },
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'A',
          });
        })
        .catch(() => {});
      // #endregion
      setCoordinator(current);
    }

    // S'abonner aux changements (remplace le polling)
    const unsubscribe = appService.subscribe(newCoordinator => {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'useAppCoordinator.ts:165',
            message: 'useAppCoordinatorOptional subscription callback',
            data: {
              coordinatorAvailable: newCoordinator !== null,
              wasNull: coordinator === null,
            },
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'A',
          });
        })
        .catch(() => {});
      // #endregion
      setCoordinator(prev => {
        if (newCoordinator !== prev) {
          // #region agent log
          import('../utils/debugLogger')
            .then(({ debugLog }) => {
              debugLog({
                location: 'useAppCoordinator.ts:177',
                message: 'useAppCoordinatorOptional state updated from subscription',
                data: {
                  prevWasNull: prev === null,
                  newIsAvailable: newCoordinator !== null,
                },
                sessionId: 'debug-session',
                runId: 'run1',
                hypothesisId: 'A',
              });
            })
            .catch(() => {});
          // #endregion
          return newCoordinator;
        }
        return prev;
      });
    });

    return () => {
      unsubscribe();
    };
  }, []); // Pas de dépendances - on veut que l'effet ne s'exécute qu'une fois

  // #region agent log
  import('../utils/debugLogger')
    .then(({ debugLog }) => {
      debugLog({
        location: 'useAppCoordinator.ts:245',
        message: 'useAppCoordinatorOptional returning',
        data: {
          coordinatorAvailable: coordinator !== null,
        },
        sessionId: 'debug-session',
        runId: 'run1',
        hypothesisId: 'A',
      });
    })
    .catch(() => {});
  // #endregion

  return coordinator;
}

/**
 * Hook pour vérifier si AppCoordinator est disponible
 * CORRIGÉ: Se met à jour automatiquement quand AppCoordinator devient disponible
 * @returns boolean
 */
export function useAppCoordinatorAvailable(): boolean {
  const [isAvailable, setIsAvailable] = useState(() => appService.isAvailable());

  useEffect(() => {
    // Vérifier immédiatement
    const currentAvailable = appService.isAvailable();
    if (currentAvailable !== isAvailable) {
      setIsAvailable(currentAvailable);
    }

    // S'abonner aux changements (remplace le polling)
    const unsubscribe = appService.subscribe(coordinator => {
      const newAvailable = coordinator !== null;
      setIsAvailable(prev => (newAvailable !== prev ? newAvailable : prev));
    });

    return () => {
      unsubscribe();
    };
  }, [isAvailable]);

  return isAvailable;
}
